import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/ui/sheet";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import {
  Position,
  positionFieldName,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import { AhuComponentsInspectionSchema } from "@/services/backend/htz/ahu/components/ahu-components-schema";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  AirHandlingUnitData,
  ComponentData,
} from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { AhuComponentsInspectionTable } from "@/services/backend/htz/ahu/components/ahu-components-table";
import {
  AddStructuralAssessmentButton,
  StructuralInspection,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/structural-inspection";
import { useState } from "react";
import { Switch } from "@/shared/components/ui/switch";
import { Label } from "@/shared/components/ui/label";
import { Camera, CheckCircle, Eye, Tags } from "lucide-react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shared/components/ui/resizable";
import {
  AddVisualAssessmentButton,
  VisualInspection,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/visual-inspection";
import {
  AddPhotoButton,
  PhotoDocumentation,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/photo-documentation";
import {
  AddMicrobialAssessmentButton,
  MicrobialInspection,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/microbial-inspection";
import { useHtzAirHandlingUnitDataShowQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import { useActiveAhuComponentCatalog } from "@/services/backend/htz/ahu/hooks/use-active-ahu-component-catalog";
import {
  ErrValidationFailedContextProvider,
  useHasFieldErrors,
} from "@/shared/lib/errors/err-validation-failed-context";
import { cn } from "@/shared/lib/utils";
import { AhuDataEditCard } from "@/services/backend/htz/ahu/components/ahu-data-edit-card";

/**
 * InspectionSheet inspects the air handling unit of a work order position.
 * In the mode "Aufnahme" the components of the air handling unit are
 * recorded, in the mode "Inspektion" a component is selected to add
 * assessments for it.
 *
 * position is rendered from the cache.
 */
export function InspectionSheet({
  workOrder,
  position,
  disabled: propDisabled,
}: {
  workOrder: WorkOrder;
  position: Position;
  disabled?: boolean;
}) {
  const [active, setActive] = useState(false);
  const [activeComponent, setActiveComponent] = useState<ComponentData | null>(
    null,
  );

  const disabled =
    !position.ahuDataId || isImmutable(workOrder) || propDisabled;

  const { hasErrors: hasError } = useHasFieldErrors(
    [
      "structuralAssessments",
      "visualAssessments",
      "microbialAssessments",
      "photos",
    ].map((list) => positionFieldName(position, list)),
  );

  return (
    <Sheet>
      <SheetTrigger asChild disabled={disabled}>
        <Button
          size="sm"
          variant="ghost"
          className={cn("h-8", hasError && "border border-red-500 shadow-md")}
        >
          {t("Inspektion")}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="h-screen space-y-4 px-4"
        // react-resizable-panels v4 handles resize gestures via document-level
        // pointer/focus listeners. Radix's DismissableLayer misreads those as an
        // outside interaction and closes the sheet, so ignore dismissals that
        // originate from a resize handle.
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest("[data-slot='resizable-handle']")) {
            event.preventDefault();
          }
        }}
      >
        <InspectionSheetHeader
          position={position}
          active={active}
          onActiveChange={setActive}
        />
        <InspectionSheetMain
          workOrder={workOrder}
          position={position}
          active={active}
          activeComponent={activeComponent}
          setActiveComponent={setActiveComponent}
        />
      </SheetContent>
    </Sheet>
  );
}

function InspectionSheetHeader({
  position,
  active,
  onActiveChange,
}: {
  position: Position;
  active: boolean;
  onActiveChange: (active: boolean) => void;
}) {
  const { data: airHandlingUnit } = useHtzAirHandlingUnitDataShowQuery(
    { id: position.ahuDataId ?? "" },
    { skip: !position.ahuDataId },
  );

  return (
    <SheetHeader className="max-h-[10vh]">
      <SheetTitle className="flex justify-between pr-12">
        <span>
          {t("Inspektion für Position: ")}
          {position.number}
          {t(" - Anlage: ")}
          {airHandlingUnit?.designation}
        </span>
        <PersistenceStateUsingContext />
        <div className="flex items-center space-x-4">
          <Label>{t("Aufnahme")}</Label>
          <Switch checked={active} onCheckedChange={onActiveChange} />
          <Label>{t("Inspektion")}</Label>
        </div>
      </SheetTitle>
      <SheetDescription>
        {t(
          "Im Modus Aufnahme, können die Komponenten der Anlage zusammengebaut werden. Im Modus Inspektion können Komponenten ausgewählt werden um für diese Beurteilungen zu erstellen.",
        )}
      </SheetDescription>
    </SheetHeader>
  );
}

function InspectionSheetMain({
  workOrder,
  position,
  active,
  activeComponent,
  setActiveComponent,
}: {
  workOrder: WorkOrder;
  position: Position;
  active: boolean;
  activeComponent: ComponentData | null;
  setActiveComponent: (component: ComponentData | null) => void;
}) {
  const { data: ahuData } = useHtzAirHandlingUnitDataShowQuery(
    { id: position.ahuDataId ?? "" },
    { skip: !position.ahuDataId },
  );
  const { data: catalog } = useActiveAhuComponentCatalog(workOrder.companyId);

  if (!ahuData || !catalog) {
    return null;
  }

  if (!active) {
    // The air handling unit data is an entity of its own, its validation
    // errors must not land in the scope of the work order.
    return (
      <ErrValidationFailedContextProvider>
        <AhuDataEditCard
          ahuData={ahuData}
          catalog={catalog}
          disabled={isImmutable(workOrder)}
        />
      </ErrValidationFailedContextProvider>
    );
  }

  return (
    <ResizablePanelGroup className="max-h-[80vh] gap-4">
      <ResizablePanel>
        <InspectionAhuCard
          ahuData={ahuData}
          position={position}
          activeComponent={activeComponent}
          setActiveComponent={setActiveComponent}
        />
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel>
        <AssessmentCard
          workOrder={workOrder}
          position={position}
          activeComponent={activeComponent}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

// InspectionAhuCard shows the components of the air handling unit with
// their assessments, to select the component to inspect.
function InspectionAhuCard({
  ahuData,
  position,
  activeComponent,
  setActiveComponent,
}: {
  ahuData: AirHandlingUnitData;
  position: Position;
  activeComponent: ComponentData | null;
  setActiveComponent: (component: ComponentData | null) => void;
}) {
  const [tabValue, setTabValue] = useState<string>("schema");
  const { data: ahuComponentCatalog } = useActiveAhuComponentCatalog(
    ahuData.companyId,
  );

  if (!ahuComponentCatalog) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Anlage")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={tabValue} onValueChange={setTabValue}>
          <TabsList>
            <TabsTrigger value="schema">{t("Schema")}</TabsTrigger>
            <TabsTrigger value="list">{t("Liste")}</TabsTrigger>
          </TabsList>
          <TabsContent value="schema">
            <AhuComponentsInspectionSchema
              ahuData={ahuData}
              ahuComponentCatalog={ahuComponentCatalog}
              activeComponent={activeComponent}
              setActiveComponent={setActiveComponent}
              assessments={position}
              className="h-[57vh]"
            />
          </TabsContent>
          <TabsContent value="list" className="h-[57vh]">
            <AhuComponentsInspectionTable
              ahu={ahuData}
              activeComponent={activeComponent}
              setActiveComponent={setActiveComponent}
              assessments={position}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function AssessmentCard({
  workOrder,
  position,
  activeComponent,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
}) {
  const [tabValue, setTabValue] = useState("structural");

  const { hasErrors: hasStructuralErrs } = useHasFieldErrors(
    positionFieldName(position, "structuralAssessments"),
  );
  const { hasErrors: hasVisualErrs } = useHasFieldErrors(
    positionFieldName(position, "visualAssessments"),
  );
  const { hasErrors: hasMicrobialErrs } = useHasFieldErrors(
    positionFieldName(position, "microbialAssessments"),
  );
  const { hasErrors: hasPhotoErrs } = useHasFieldErrors(
    positionFieldName(position, "photos"),
  );

  const props = { workOrder, position, activeComponent };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {activeComponent && activeComponent.name}
          {!activeComponent && (
            <span className="text-muted-foreground font-normal italic">
              {t("- bitte Komponente auswählen -")}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tabValue} onValueChange={setTabValue}>
          <div className="flex justify-between">
            <TabsList>
              <TabsTrigger
                value="structural"
                className={cn(
                  hasStructuralErrs && "border border-red-500 shadow-md",
                )}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                <span>{t("Konstruktiv")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="visual"
                className={cn(
                  hasVisualErrs && "border border-red-500 shadow-md",
                )}
              >
                <Eye className="mr-2 h-4 w-4" />
                <span>{t("Visuell")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="microbial"
                className={cn(
                  hasMicrobialErrs && "border border-red-500 shadow-md",
                )}
              >
                <Tags className="mr-2 h-4 w-4" />
                <span>{t("Proben")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="photo"
                className={cn(
                  hasPhotoErrs && "border border-red-500 shadow-md",
                )}
              >
                <Camera className="mr-2 h-4 w-4" />
                <span>{t("Fotos")}</span>
              </TabsTrigger>
            </TabsList>
            {tabValue === "structural" && (
              <AddStructuralAssessmentButton {...props} />
            )}
            {tabValue === "visual" && <AddVisualAssessmentButton {...props} />}
            {tabValue === "microbial" && (
              <AddMicrobialAssessmentButton {...props} />
            )}
            {tabValue === "photo" && <AddPhotoButton {...props} />}
          </div>
          <TabsContent value="structural" className="h-[65vh]">
            <StructuralInspection {...props} />
          </TabsContent>
          <TabsContent value="visual" className="h-[65vh]">
            <VisualInspection {...props} />
          </TabsContent>
          <TabsContent value="microbial" className="h-[65vh]">
            <MicrobialInspection {...props} />
          </TabsContent>
          <TabsContent value="photo" className="h-[65vh]">
            <PhotoDocumentation {...props} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
